import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Form } from 'react-bootstrap';
import { Typeahead } from 'react-bootstrap-typeahead';
import 'react-bootstrap-typeahead/css/Typeahead.css';

// The values come from /values/filters; '' and '*empty*' both stand for an empty field. Values are sent as strings, like the <select> did.
function toOption(value) {
  if ((value === '') || (value === '*empty*')) return { value: '*empty*', label: 'Vide' };
  if (value === true) return { value: 'true', label: 'Oui' };
  if (value === false) return { value: 'false', label: 'Non' };
  // Only spaces: dirty data the back does not count as empty, still filterable as is.
  if (String(value).trim() === '') return { value: String(value), label: 'Vide (espaces)' };
  return { value: String(value), label: String(value) };
}

// descending: newest first, numbers compared as numbers.
export function filterOptions(values, descending = false) {
  const options = (values ?? []).filter((value) => (value !== undefined) && (value !== null)).map(toOption);
  return descending ? options.sort((a, b) => b.value.localeCompare(a.value, undefined, { numeric: true })) : options;
}

export const isActive = (selected) => (selected !== '') && (selected !== null) && (selected !== undefined);

// While the input shows the selected value, the menu lists every option, not only the one matching that text.
// A plain input, without the hint input Typeahead lays under it: it doubled the text in the sidebar and has no id nor name.
const renderInput = ({ inputRef, referenceElementRef, ...inputProps }) => (
  <Form.Control
    {...inputProps}
    size="sm"
    spellCheck={false}
    ref={(node) => {
      inputRef(node);
      referenceElementRef(node);
    }}
  />
);

export const filterBy = (option, { text, selected }) => ((selected.length > 0) && (text === selected[0].label)) || option.label.toLowerCase().includes(text.toLowerCase());

function Filter({ id, name, label, onChange }) {
  const loaded = useSelector((state) => state.enrollments.filtersLoaded);
  const disabled = useSelector((state) => state.enrollments.filtersDisabled);
  const values = useSelector((state) => state.enrollments.filters[name].values);
  const selected = useSelector((state) => state.enrollments.filters[name].selected);
  const options = useMemo(() => filterOptions(values, name === 'promos'), [ values, name ]);
  const active = isActive(selected);
  const empty = loaded && (options.length === 0);

  // Clearing, with the button or by typing over the value, sends '': no filter.
  const change = ([ option ]) => {
    const value = option?.value ?? '';
    if (value !== String(selected ?? '')) onChange({ target: { name, value } });
  };

  return (
    <li className="nav-item">
      <div className="form-group">
        <label htmlFor={id} className={active ? 'font-weight-bold' : undefined}>{label}</label>
        <Typeahead
          id={`${id}-menu`}
          inputProps={{ id, name: id }}
          renderInput={renderInput}
          options={options}
          filterBy={filterBy}
          selected={active ? options.filter(({ value }) => value === String(selected)) : []}
          onChange={change}
          clearButton
          positionFixed
          flip
          align="left"
          disabled={disabled || empty}
          placeholder={loaded ? (empty ? 'Aucune valeur' : 'Tous') : 'Chargement...'}
          emptyLabel="Aucun résultat"
          paginationText="Afficher plus de résultats"
        />
      </div>
    </li>
  );
}

export default Filter;
